/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice } from '@reduxjs/toolkit';

import { createSelector, createSliceHook, makeActionCreator } from '../helpers';

import {
  ReaderSubTaskState,
  ReaderSubTaskFetchAction,
  ReaderSubTaskSuccessAction,
  ReaderSubTaskSaveAction,
  ReaderSubTaskCompleteAction,
  ReaderSubTaskStartAction,
  ReaderSubTaskSaveOrCompleteSuccessAction,
} from './readerSubTask.types';

const initialState: ReaderSubTaskState = {
  data: {},
  currentSubTaskId: undefined,
  saving: false,
  error: undefined,
  savingError: undefined,
  loading: true,
};

const { act, actReject, actReq, actResolve, actEmpty } =
  makeActionCreator<ReaderSubTaskState>();

export const readerSubTaskSlice = createSlice({
  name: 'readerSubTask',
  initialState,
  reducers: {
    fetchReaderSubTask: actReq<ReaderSubTaskFetchAction['payload']>(),
    fetchReaderSubTaskSuccess: act<ReaderSubTaskSuccessAction['payload']>(
      (task, state) => ({
        data: {
          ...state.data,
          [task?.id ?? '']: task ?? {},
        },
        currentSubTaskId: task?.id ?? undefined,
        loading: false,
      })
    ),
    fetchReaderSubTaskFailure: actReject(),

    startReaderSubTask: actReq<ReaderSubTaskStartAction['payload']>('saving'),
    startReaderSubTaskSuccess: actResolve<string>(null, 'saving'),
    startReaderSubTaskFailure: actReject('savingError', 'saving'),

    saveReaderSubTask: actReq<ReaderSubTaskSaveAction['payload']>('saving'),
    saveReaderSubTaskSuccess: actResolve<
      ReaderSubTaskSaveOrCompleteSuccessAction['payload']
    >(null, 'saving'),
    saveReaderSubTaskFailure: actReject('savingError', 'saving'),

    completeReaderSubTask:
      actReq<ReaderSubTaskCompleteAction['payload']>('saving'),
    completeReaderSubTaskSuccess: actResolve<
      ReaderSubTaskSaveOrCompleteSuccessAction['payload']
    >(null, 'saving'),
    completeReaderSubTaskFailure: actReject('savingError', 'saving'),

    reset: () => {
      return initialState;
    },
  },

  selectors: {
    selectState: state => state,
    selectReaderSubTasks: state => state.data,
    selectCurrentVisit: state =>
      state.currentSubTaskId
        ? state.data[state.currentSubTaskId]?.visits?.find(
            v =>
              v.visitId ===
              state.data[state.currentSubTaskId as string]?.visitId
          )
        : undefined,
    selectCurrentReaderSubTask: state =>
      state.currentSubTaskId ? state.data[state.currentSubTaskId] : undefined,
  },
});

export const {
  fetchReaderSubTask,
  fetchReaderSubTaskSuccess,
  fetchReaderSubTaskFailure,
  startReaderSubTask,
  startReaderSubTaskSuccess,
  startReaderSubTaskFailure,
} = readerSubTaskSlice.actions;

export const actions = { ...readerSubTaskSlice.actions };

export const useReaderSubTaskSlice = createSliceHook(
  readerSubTaskSlice.name,
  actions
);

export const selectReaderSubTaskState = createSelector('readerSubTask');
